<template>
  <div id="app">
    <RouterView />
    <Toaster position="top-right" :theme="theme" :expand="true" class="pointer-events-auto" />
    <NetworkOfflineModal
      :open="isOffline && showOfflineModal"
      @update:open="(value) => (showOfflineModal = value)"
    />
    <NewVersionModal
      v-if="showNewVersionModal"
      @reload="handleReloadApp"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch, ref } from "vue"
import { useAuthStore } from "@/stores/auth"
import { useRestaurantStore } from "@/stores/restaurant"
import { useRouter } from "vue-router"
import { Toaster } from "@/components/ui/sonner"
import { webSocketService } from "@/services/websocket"
import { useTheme } from "@/composables/useTheme"
import { useNetworkStatus } from "@/composables/useNetworkStatus"
import { usePageVisibility } from "@/composables/usePageVisibility"
import { useActiveOrdersPolling } from "@/composables/useActiveOrdersPolling"
import { pushNotificationService } from "@/services/pushNotification"
import { apiService } from "@/services/api"
import NetworkOfflineModal from "@/components/NetworkOfflineModal.vue"
import NewVersionModal from "@/components/NewVersionModal.vue"
import { toast } from "vue-sonner"
import { useI18n } from "@/composables/useI18n"
import { useVersionCheck } from "@/composables/useVersionCheck"
import { primeNotificationSound, playNotificationSound } from "@/services/notificationSound"
import "vue-sonner/style.css"

/* global navigator, window */

const authStore = useAuthStore()
const restaurantStore = useRestaurantStore()
const router = useRouter()

// Initialize theme (this will apply theme from localStorage on mount)
const { theme } = useTheme()
const { t } = useI18n()

// Network status monitoring
const { isOnline, isOffline, checkConnection } = useNetworkStatus()
const showOfflineModal = ref(false)

// Version update monitoring
const showNewVersionModal = ref(false)

// Sprawdzanie dostępności nowej wersji aplikacji (tylko w produkcji)
useVersionCheck({
  enabled: import.meta.env.PROD,
  onNewVersionAvailable: () => {
    // eslint-disable-next-line no-console
    console.log("🔄 [App] New version of the application is available")
    showNewVersionModal.value = true
  },
  // Co 60 sekund
  intervalMs: 60_000,
})

// Page visibility monitoring (automatic refresh on tab return)
usePageVisibility()

// Watch for offline status to show modal
watch(isOffline, (newValue) => {
  if (newValue && authStore.isAuthenticated && router.currentRoute.value.path !== "/login") {
    showOfflineModal.value = true
    // eslint-disable-next-line no-console
    console.log("🌐 [App] Network offline, showing modal")
  } else if (!newValue) {
    // Connection restored - automatically reconnect
    if (showOfflineModal.value) {
      handleReconnect()
    }
  }
})

// Watch for online status to automatically reconnect
watch(isOnline, async (newValue) => {
  if (newValue && showOfflineModal.value) {
    // Connection restored - verify and reconnect
    const isActuallyOnline = await checkConnection()
    if (isActuallyOnline) {
      handleReconnect()
    }
  }
})

// Handle reconnection after network restore
const handleReconnect = async () => {
  if (!restaurantStore.restaurant?.id) {
    return
  }

  try {
    // eslint-disable-next-line no-console
    console.log("🔄 [App] Checking health endpoint after network restore...")

    // Check health endpoint to verify connection
    const healthResponse = await apiService.get<{ status: string }>("/health")
    
    if (healthResponse.status !== "ok") {
      throw new Error("Health check failed")
    }

    // eslint-disable-next-line no-console
    console.log("✅ [App] Health check passed, connection restored")

    const packageType = restaurantStore.restaurant.packageType

    // For FULL package, also refresh orders
    if (packageType === "FULL" || !packageType) {
      // eslint-disable-next-line no-console
      console.log("🔄 [App] Refreshing orders for FULL package")
      
      await Promise.all([
        restaurantStore.fetchActiveOrders(restaurantStore.restaurant.id),
        restaurantStore.fetchOrders(restaurantStore.restaurant.id, 1, 10),
      ])

      toast.success(t("common.messages.connectionRestored"), {
        description: t("common.messages.dataRefreshed"),
        duration: 3000,
      })
    } else {
      // eslint-disable-next-line no-console
      console.log("⏭️ [App] Skipping orders refresh for SIMPLE package")
      
      toast.success(t("common.network.connectionRestored"), {
        description: t("common.network.connectionRestoredDescription"),
        duration: 3000,
      })
    }

    // Check and reconnect websocket if needed
    if (!webSocketService.socket?.connected) {
      // eslint-disable-next-line no-console
      console.log("🔄 [App] Reconnecting websocket...")
      webSocketService.disconnect()
      webSocketService.connect()
    }

    // Close modal (allow closing since connection is restored)
    showOfflineModal.value = false
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("❌ [App] Error checking health or reconnecting:", error)
    toast.error(t("common.network.connectionError"), {
      description: t("common.network.connectionErrorRetry"),
    })
  }
}

const handleReloadApp = () => {
  // Pełne przeładowanie aplikacji aby wczytać nową wersję
  // eslint-disable-next-line no-undef
  window.location.reload()
}

onMounted(async () => {
  // Cloudflare Pages environment variables
  // eslint-disable-next-line no-console
  console.table({
    CF_PAGES_COMMIT_SHA: import.meta.env?.VITE_CF_PAGES_COMMIT_SHA,
    CF_PAGES: import.meta.env?.VITE_CF_PAGES,
    CF_PAGES_URL: import.meta.env?.VITE_CF_PAGES_URL,
    CF_PAGES_BRANCH: import.meta.env?.VITE_CF_PAGES_BRANCH,
  })

  // Listen for messages from Service Worker (notification clicks)
  if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      // eslint-disable-next-line no-console
      console.log("📨 [App] Message from Service Worker:", event.data)
      
      if (event.data?.type === "NOTIFICATION_CLICKED") {
        const { url, data: notificationData } = event.data
        
        // Navigate to target URL if user is authenticated
        if (authStore.isAuthenticated && url) {
          // eslint-disable-next-line no-console
          console.log("🔔 [App] Navigating to notification target:", url)
          router.push(url).catch((error) => {
            // eslint-disable-next-line no-console
            console.warn("⚠️ [App] Navigation error:", error)
          })
          
          // Refresh orders if it's an order-related notification
          if (notificationData?.type && restaurantStore.restaurant?.id) {
            // Skip refresh for SIMPLE package (no orders functionality)
            const packageType = restaurantStore.restaurant.packageType
            if (packageType === "SIMPLE") {
              // eslint-disable-next-line no-console
              console.log("⏭️ [App] Skipping orders refresh for SIMPLE package")
              return
            }
            
            if (["new_order", "payment_completed", "order_status_changed"].includes(notificationData.type)) {
              // eslint-disable-next-line no-console
              console.log("🔄 [App] Refreshing orders after notification click")
              Promise.all([
                restaurantStore.fetchActiveOrders(restaurantStore.restaurant.id),
                restaurantStore.fetchOrders(restaurantStore.restaurant.id, 1, 10),
              ]).catch((error) => {
                // eslint-disable-next-line no-console
                console.warn("⚠️ [App] Error refreshing orders:", error)
              })
            }
          }
        }
      } else if (event.data?.type === "NOTIFICATION_RECEIVED") {
        const notificationData = event.data.data

        // Only handle sounds for authenticated users
        if (!authStore.isAuthenticated) {
          return
        }

        // Avoid double sounds: if WebSocket is connected, WebSocket handlers
        // are responsible for playing sounds. Use push only as a fallback.
        if (webSocketService.socket?.connected) {
          return
        }

        if (
          notificationData?.type &&
          ["new_order", "payment_completed", "waiter_called"].includes(
            notificationData.type
          )
        ) {
          // eslint-disable-next-line no-console
          console.log(
            "🔔 [App] Playing notification sound from NOTIFICATION_RECEIVED:",
            notificationData.type
          )
          playNotificationSound()
        }
      }
    })
  }

  // Prime notification sound on first user interaction (click/touch)
  if (typeof window !== "undefined") {
    const primeHandler = () => {
      primeNotificationSound()
      window.removeEventListener("click", primeHandler)
      window.removeEventListener("touchstart", primeHandler)
    }

    window.addEventListener("click", primeHandler, { once: true })
    window.addEventListener("touchstart", primeHandler, { once: true })
  }

  // Inicjalizuj autoryzację
  await authStore.initializeAuth()

  // Jeśli użytkownik jest zalogowany, zainicjalizuj restaurację
  if (authStore.isAuthenticated) {
    await restaurantStore.initializeUserRestaurant()

    // Połącz z WebSocket
    webSocketService.connect()

    // Initialize active orders polling (fallback when WebSocket is disconnected)
    useActiveOrdersPolling()

    // Initialize push notifications (only if user is authenticated)
    // This requires user gesture on iOS, so we'll try but handle gracefully if it fails
    if (pushNotificationService.isSupported()) {
      try {
        // Initialize push notifications in background (don't block app startup)
        pushNotificationService.initialize().catch((error) => {
          // eslint-disable-next-line no-console
          console.warn("⚠️ [App] Push notifications initialization failed (non-critical):", error)
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("⚠️ [App] Push notifications not available:", error)
      }
    }
  }

  // Sprawdź czy użytkownik jest zalogowany tylko jeśli nie jest na stronie logowania
  if (!authStore.isAuthenticated && router.currentRoute.value.path !== "/login") {
    router.push("/login")
  }
})

onUnmounted(() => {
  // Cleanup push notifications if needed
  if (authStore.isAuthenticated) {
    pushNotificationService.cleanup().catch((error) => {
      // eslint-disable-next-line no-console
      console.warn("⚠️ [App] Error cleaning up push notifications:", error)
    })
  }
})
</script>
